import { useQuery } from '@tanstack/react-query';
import { FC, useMemo } from 'react';
import { Control, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import Text from '@/components/Text';
import { api } from '@/utils/api';

import { FormDataT } from './DistributionScheduleModal';

const Wrap = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Box = styled.div`
  border-radius: 4px;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
`;

const Description = styled(Text)`
  margin-top: 12px;

  strong {
    font-weight: 700;
  }
`;

const NumbersBox = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

interface Props {
  control: Control<FormDataT>;
  name: `channels.${number}.id` | 'channel_id';
}

const ReelsCounter: FC<Props> = ({ control, name }) => {
  const channelId = useWatch<FormDataT, `channels.${number}.id` | 'channel_id'>(
    {
      control,
      name,
    },
  );

  const isReel = !!useWatch<FormDataT, 'is_reel'>({
    control,
    name: 'is_reel',
  });

  const { data, isLoading } = useQuery(
    [
      'reels_counter',
      {
        channel_id: channelId,
      },
    ],
    () =>
      api.get<never, { releasedCount: number }>(
        '/v1/distribution/reels-counter',
        {
          params: {
            channel_id: channelId,
          },
        },
      ),
    {
      staleTime: 30_000,
      enabled: !!channelId && isReel,
    },
  );

  const remaining = useMemo(() => {
    if (!data) {
      return 0;
    }

    const r = 30 - data.releasedCount;

    return r >= 0 ? r : 0;
  }, [data]);

  if (!isReel) {
    return null;
  }

  if (!channelId) {
    return null;
  }

  return (
    <Wrap>
      <Box>
        <Text $type="smallBold">Reels Limit Calculator</Text>
        <Description $color="gray" $type="smallNormal">
          Facebook Reels are limited to{' '}
          <strong>30 posts within a 24-hour</strong> period for one page.{' '}
          <strong>Please note:</strong> Videos with scheduled status are also
          counted towards this limit.
        </Description>
      </Box>
      <NumbersBox>
        {isLoading ? (
          <Text $type="button">Loading...</Text>
        ) : (
          <>
            <Text $type="button" $color={remaining ? 'black' : 'error'}>
              {remaining}
            </Text>
            <Text $color="gray" $type="extraSmall">
              Reels remaining
            </Text>
          </>
        )}
      </NumbersBox>
    </Wrap>
  );
};

export default ReelsCounter;
